import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { User, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { trackLogin, trackSignUp } from '../utils/analytics';

export const MemberLogin = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: ''
  });

  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect');
  const safeRedirect = redirectTo && redirectTo.startsWith('/') && !redirectTo.startsWith('//')
    ? redirectTo : null;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const data = await login(formData.email, formData.password);
        trackLogin('email');
        // Force-change flow: when an admin generates a temporary password,
        // the member must rotate it before doing anything else.
        if (data?.user?.must_change_password) {
          toast.message('Please change your temporary password to continue.');
          navigate('/change-password');
          return;
        }
        toast.success('Welcome back!');
        if (safeRedirect) {
          navigate(safeRedirect);
        } else {
          const access = data?.user?.access;
          if (access === 'Administrator' || access === 'Admin' || access === 'Webmaster') {
            navigate('/admin/registrations');
          } else {
            navigate('/members');
          }
        }
      } else {
        if (!formData.firstName || !formData.lastName) {
          toast.error('Please enter your first and last name');
          setLoading(false);
          return;
        }
        await register(formData.email, formData.password, formData.firstName, formData.lastName);
        trackSignUp('email');
        toast.success('Account created successfully!');
        navigate(safeRedirect || '/members');
      }
    } catch (error) {
      toast.error(error.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white py-16" data-testid="member-login-page">
      <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-serif text-stone-900 mb-2">Member Area</h1>
          <p className="text-stone-600">
            {isLogin ? 'Sign in to access your account' : 'Create your member account'}
          </p>
        </div>

        <Card className="border-stone-200 shadow-lg">
          <CardHeader className="pb-4">
            <div className="flex border-b border-stone-200">
              <button
                className={`flex-1 py-3 text-center font-medium transition-colors ${
                  isLogin 
                    ? 'text-cafa-red border-b-2 border-cafa-red' 
                    : 'text-stone-500 hover:text-stone-700'
                }`}
                onClick={() => setIsLogin(true)}
                data-testid="login-tab"
              >
                Sign In
              </button>
              <button
                className={`flex-1 py-3 text-center font-medium transition-colors ${
                  !isLogin 
                    ? 'text-cafa-red border-b-2 border-cafa-red' 
                    : 'text-stone-500 hover:text-stone-700'
                }`}
                onClick={() => setIsLogin(false)}
                data-testid="register-tab"
              >
                Create Account
              </button>
            </div>
          </CardHeader>

          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
                      <Input
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required={!isLogin}
                        className="pl-10"
                        placeholder="John"
                        data-testid="register-firstname"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required={!isLogin}
                      placeholder="Doe"
                      data-testid="register-lastname"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="pl-10"
                    placeholder="john@example.com"
                    data-testid="login-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="pl-10 pr-10"
                    placeholder="••••••••"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                    data-testid="login-password-visibility-button"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-cafa-red hover:bg-cafa-red-900 text-white"
                disabled={loading}
                data-testid="auth-submit"
              >
                {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
              </Button>

              {isLogin && (
                <div className="text-center">
                  <Link 
                    to="/forgot-password" 
                    className="text-sm text-cafa-red hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Forgot your password?
                  </Link>
                </div>
              )}
            </form>

            {isLogin && (
              <p className="text-center text-sm text-stone-500 mt-6">
                Not a member yet?{' '}
                <Link to="/membership" className="text-cafa-red hover:underline" data-testid="login-membership-link">
                  Join CAFA
                </Link>
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
